/**
 * PVSummaryChart_V2 - Daily Solar PV Summary Chart
 *
 * Displays 15-minute interval solar data with:
 * - Stacked bars: grid consumption + solar self-consumption
 * - Separate bars: energy sold to grid
 * - Price overlay lines (spot + day average)
 * - Summary InfoBox with daily PV totals from metadata
 *
 * Data source: V2 timeseries snapshot.solar (production, self_consumption, export, import)
 */

import React, { useMemo } from 'react';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer
} from 'recharts';
import { InfoBox } from '../../Components';
import { formatKWh } from '../ChartUtils';
import {
  extractTimeseries,
  getHourlyTicks,
  formatHourLabel,
  getResponsiveBarSize,
  aggregateToHourly
} from './heatingChartUtils';
import { t } from '../../i18n';

const PVSummaryChart_V2 = ({ data, metadata, isMobile = false, showDetails = false }) => {

  const timeseries = useMemo(() => extractTimeseries(data), [data]);

  const chartData = useMemo(() => {
    const shouldAggregate = isMobile && !showDetails;
    const source = shouldAggregate ? aggregateToHourly(timeseries) : timeseries;

    return source.map(snapshot => {
      const solar = snapshot.solar || {};
      const pricing = snapshot.pricing || {};

      const selfConsumed = parseFloat(solar.self_consumption || 0);
      const exported = parseFloat(solar.export || 0);
      const gridImport = parseFloat(solar.import || 0);

      return {
        timestamp: snapshot.timestamp,
        solarConsumption: selfConsumed,
        gridConsumption: gridImport,
        soldEnergy: exported,
        panelPower: solar.panel_power != null ? parseInt(solar.panel_power) : null,
        price: parseFloat(pricing.price || 0),
        dayAvg: parseFloat(pricing.day_avg || 0),
        belowAvg: pricing.below_avg === 1 || pricing.below_avg === true
      };
    });
  }, [timeseries, isMobile, showDetails]);

  const hourlyTicks = useMemo(() => getHourlyTicks(chartData), [chartData]);
  const barSize = useMemo(() => getResponsiveBarSize(800, chartData.length), [chartData.length]);

  if (!chartData.length) return null;

  const summaryPoints = [
    ...(metadata.total_panel_prod_for_day != null
      ? [{ label: t('heatingChart.solarProduction'), value: formatKWh(metadata.total_panel_prod_for_day) }]
      : []),
    ...(metadata.total_consumption_from_panels != null
      ? [{ label: t('heatingChart.solarConsumption'), value: formatKWh(metadata.total_consumption_from_panels) }]
      : []),
    ...(metadata.total_sold_kwh != null
      ? [{ label: t('heatingChart.energySold'), value: formatKWh(metadata.total_sold_kwh) }]
      : []),
    ...(metadata.total_received_kwh != null
      ? [{ label: t('heatingChart.gridEnergy'), value: formatKWh(metadata.total_received_kwh) }]
      : []),
    ...(metadata.total_income != null
      ? [{ label: t('heatingChart.totalIncome'), value: `c ${metadata.total_income.toFixed(2)}` }]
      : []),
    ...(metadata.total_cost_of_received != null
      ? [{ label: t('heatingChart.costOfReceived'), value: `c ${metadata.total_cost_of_received.toFixed(2)}` }]
      : []),
  ];

  return (
    <>
      <ResponsiveContainer width="100%" height={300}>
        <ComposedChart data={chartData} margin={{ top: 10, right: 30, bottom: 10, left: 10 }}>
          <CartesianGrid stroke="#f5f5f5" />
          <XAxis
            dataKey="timestamp"
            ticks={hourlyTicks}
            tickFormatter={formatHourLabel}
          />
          <YAxis
            yAxisId="left"
            label={{ value: t('heatingChart.energy'), angle: -90, position: 'insideLeft', style: { fontSize: 11 } }}
          />
          <YAxis
            yAxisId="right"
            orientation="right"
            label={{ value: t('heatingChart.price'), angle: 90, position: 'insideRight', style: { fontSize: 11 } }}
          />
          <Tooltip content={<PVTooltip />} />
          <Legend />

          {/* Stacked consumption: grid + solar */}
          <Bar yAxisId="left" dataKey="gridConsumption" name={t('heatingChart.gridConsumption')}
               stackId="consumption" fill="#4fc3f7" barSize={barSize} />
          <Bar yAxisId="left" dataKey="solarConsumption" name={t('heatingChart.solarConsumption')}
               stackId="consumption" fill="#81c784" barSize={barSize} />

          {/* Sold to grid */}
          <Bar yAxisId="left" dataKey="soldEnergy" name={t('heatingChart.energySold')}
               fill="#ff9800" barSize={barSize} />

          {/* Price lines */}
          <Line type="step" yAxisId="right" dataKey="price" name={t('heatingChart.electricityPrice')}
                stroke="#ff7300" strokeWidth={2} dot={false} />
          <Line yAxisId="right" dataKey="dayAvg" name={t('heatingChart.dayAverage')}
                stroke="#ffa089" strokeDasharray="8" strokeWidth={1} dot={false} />
        </ComposedChart>
      </ResponsiveContainer>
      {summaryPoints.length > 0 && (
        <InfoBox title={t('heatingChart.solarSummary')} dataPoints={summaryPoints} />
      )}
    </>
  );
};

const PVTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;

  return (
    <div style={{
      background: 'white', padding: '10px', border: '1px solid #ccc',
      borderRadius: '4px', fontSize: '12px'
    }}>
      <p><strong>{formatHourLabel(label)}</strong></p>
      {d.gridConsumption > 0 && <p style={{ color: '#4fc3f7' }}>{t('heatingChart.gridConsumption')}: {formatKWh(d.gridConsumption)}</p>}
      {d.solarConsumption > 0 && <p style={{ color: '#81c784' }}>{t('heatingChart.solarConsumption')}: {formatKWh(d.solarConsumption)}</p>}
      {d.soldEnergy > 0 && <p style={{ color: '#ff9800' }}>{t('heatingChart.energySold')}: {formatKWh(d.soldEnergy)}</p>}
      {d.panelPower != null && <p>Panel: {d.panelPower} W</p>}
      <p>{t('heatingChart.electricityPrice')}: {d.price.toFixed(2)} c/kWh</p>
    </div>
  );
};

export default PVSummaryChart_V2;
